/**
 * Recurrence engine for repeated activity sessions.
 *
 * Given an anchor session (start/end ISO UTC) and a Repeat configuration, this module
 * expands the series into N concrete ActivitySession occurrences. Time semantics are
 * anchored to PT wall-clock (see PRD docs/prd-repeated-sessions.md §4.2):
 *   - Each occurrence preserves the anchor's PT hour/minute, not UTC offset.
 *   - DST shifts the UTC offset; wall-clock stays put. The end is anchor-end's PT
 *     wall-clock applied to the same target date.
 *
 * The engine enforces the same constraints the backend validates:
 *   - per-series cap of 100 (anchor counts as #1).
 *   - 5-year calendar horizon from anchor (with Feb 29 → Feb 28 clamping).
 *   - On Date: anchor + 1 ~ anchor + 365 days (PT calendar).
 *   - Custom Weekly must include ≥1 weekday.
 *
 * `expandSeries` returns either an error string or the materialized occurrences.
 * Errors surface to the form layer for inline display and toast.
 */

import { ptInstantParts, ptWallToUtc } from './timezone';

export const MAX_SESSIONS_PER_SERIES = 100;
export const SERIES_HORIZON_YEARS = 5;
export const SERIES_END_ON_DATE_MIN_OFFSET_DAYS = 1;
export const SERIES_END_ON_DATE_MAX_OFFSET_DAYS = 365;
export const CUSTOM_INTERVAL_MIN = 1;
export const CUSTOM_INTERVAL_MAX = 99;
export const AFTER_N_MIN = 2;
export const AFTER_N_MAX = MAX_SESSIONS_PER_SERIES;

// ─── Public API types ─────────────────────────────────────────────────────────

export type RepeatMode = 'NEVER' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';
export type CustomFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';
/** 0 = Sunday, 1 = Monday, …, 6 = Saturday. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface CustomConfig {
  frequency: CustomFrequency;
  /** Interval. 1 ≤ every ≤ 99. */
  every: number;
  /** Required when frequency === 'WEEKLY'. Each entry is 0 (Sun) … 6 (Sat). */
  weekdays?: Weekday[];
}

export type EndRepeat =
  | { mode: 'AFTER'; n: number }
  /** ON_DATE: PT-local YYYY-MM-DD, inclusive (occurrences ≤ this date are kept). */
  | { mode: 'ON_DATE'; dateISO: string };

export interface RepeatConfig {
  mode: RepeatMode;
  custom?: CustomConfig;
  endRepeat?: EndRepeat;
}

export interface AnchorSession {
  /** UTC ISO string. */
  startTime: string;
  /** UTC ISO string. */
  endTime: string;
}

export type ExpandResult =
  | { ok: true; occurrences: AnchorSession[] }
  | { ok: false; error: string };

// ─── Implementation ──────────────────────────────────────────────────────────

interface YMD {
  year: number;
  month: number;
  day: number;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const DAYS_IN_MONTH: Record<number, number> = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
};

function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) return 29;
  return DAYS_IN_MONTH[month];
}

function compareYmd(a: YMD, b: YMD): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

function addDaysYmd(d: YMD, days: number): YMD {
  // UTC math then read components — independent of browser timezone.
  const base = Date.UTC(d.year, d.month - 1, d.day);
  const shifted = new Date(base + days * 24 * 60 * 60 * 1000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/** Difference in calendar days (b - a). */
function diffDaysYmd(a: YMD, b: YMD): number {
  const aMs = Date.UTC(a.year, a.month - 1, a.day);
  const bMs = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((bMs - aMs) / (24 * 60 * 60 * 1000));
}

/** PT weekday for a YMD (0 = Sun, …, 6 = Sat). */
function weekdayOfYmd(d: YMD): number {
  // We can compute weekday via UTC math because the day-of-week mapping is identical
  // across all timezones for the same calendar date (it's a property of the date itself).
  return new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay();
}

/** Add `years` to a YMD with Feb 29 → Feb 28 clamping when target year is not leap. */
function addYearsYmd(d: YMD, years: number): YMD {
  const targetYear = d.year + years;
  let targetDay = d.day;
  if (d.month === 2 && d.day === 29 && !isLeapYear(targetYear)) {
    targetDay = 28;
  }
  return { year: targetYear, month: d.month, day: targetDay };
}

/** Parse a PT-local YYYY-MM-DD string; returns null if invalid. */
function parsePtDate(dateStr: string): YMD | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

// ─── Main entry ──────────────────────────────────────────────────────────────

/**
 * Expand an anchor session + repeat configuration into a list of concrete sessions.
 *
 * On NEVER mode, returns just the anchor as-is.
 *
 * Returns `{ ok: false, error }` for validation errors and silently caps the series at
 * MAX_SESSIONS_PER_SERIES or the 5-year horizon (also surfaced as errors when the cap
 * would truncate generation).
 */
export function expandSeries(anchor: AnchorSession, config: RepeatConfig): ExpandResult {
  const anchorStartUtc = new Date(anchor.startTime);
  const anchorEndUtc = new Date(anchor.endTime);
  if (isNaN(anchorStartUtc.getTime()) || isNaN(anchorEndUtc.getTime())) {
    return { ok: false, error: 'Anchor session has invalid start/end time' };
  }
  if (anchorEndUtc.getTime() <= anchorStartUtc.getTime()) {
    return { ok: false, error: 'Anchor end time must be after start time' };
  }

  if (config.mode === 'NEVER') {
    return {
      ok: true,
      occurrences: [{ startTime: anchor.startTime, endTime: anchor.endTime }],
    };
  }

  const anchorPt = ptInstantParts(anchorStartUtc);
  const anchorEndPt = ptInstantParts(anchorEndUtc);
  const anchorYmd: YMD = { year: anchorPt.year, month: anchorPt.month, day: anchorPt.day };
  const anchorEndYmd: YMD = {
    year: anchorEndPt.year,
    month: anchorEndPt.month,
    day: anchorEndPt.day,
  };
  // How many PT calendar days the anchor session spans (0 for same-day, 1 for "ends next day", etc.).
  // Each occurrence preserves the anchor's PT end wall-clock on (target date + endDayOffset).
  // This is correct for normal anchors (same offset every occurrence) and for the edge case where
  // the anchor itself spans a DST transition (e.g., 01:30 → 03:30 PT on spring-forward day): the
  // wall-clock end time is preserved on each occurrence rather than the elapsed UTC duration.
  const endDayOffset = diffDaysYmd(anchorYmd, anchorEndYmd);

  // ── Validate end-repeat ────────────────────────────────────────────────────
  const endRepeat = config.endRepeat;
  if (!endRepeat) {
    return { ok: false, error: 'End repeat is required when Repeat is not Never' };
  }

  let endByDate: YMD | null = null;
  let endByCount: number | null = null;

  if (endRepeat.mode === 'AFTER') {
    if (!Number.isInteger(endRepeat.n) || endRepeat.n < AFTER_N_MIN || endRepeat.n > AFTER_N_MAX) {
      return {
        ok: false,
        error: `After N must be an integer between ${AFTER_N_MIN} and ${AFTER_N_MAX}`,
      };
    }
    endByCount = endRepeat.n;
  } else {
    const onDate = parsePtDate(endRepeat.dateISO);
    if (!onDate) {
      return { ok: false, error: 'On Date must be a valid YYYY-MM-DD' };
    }
    const offset = diffDaysYmd(anchorYmd, onDate);
    if (offset < SERIES_END_ON_DATE_MIN_OFFSET_DAYS) {
      return { ok: false, error: 'On Date must be after the anchor date' };
    }
    if (offset > SERIES_END_ON_DATE_MAX_OFFSET_DAYS) {
      return {
        ok: false,
        error: `On Date can be at most ${SERIES_END_ON_DATE_MAX_OFFSET_DAYS} days after the anchor`,
      };
    }
    endByDate = onDate;
  }

  // ── Validate CUSTOM config ─────────────────────────────────────────────────
  if (config.mode === 'CUSTOM') {
    const custom = config.custom;
    if (!custom) {
      return { ok: false, error: 'Custom config is required for Custom mode' };
    }
    if (
      !Number.isInteger(custom.every) ||
      custom.every < CUSTOM_INTERVAL_MIN ||
      custom.every > CUSTOM_INTERVAL_MAX
    ) {
      return {
        ok: false,
        error: `Custom "every" must be an integer between ${CUSTOM_INTERVAL_MIN} and ${CUSTOM_INTERVAL_MAX}`,
      };
    }
    if (custom.frequency === 'WEEKLY') {
      if (!custom.weekdays || custom.weekdays.length === 0) {
        return { ok: false, error: 'Custom Weekly requires at least one weekday' };
      }
    }
  }

  // ── Compute target dates (PT calendar) ─────────────────────────────────────
  const horizonEnd = addYearsYmd(anchorYmd, SERIES_HORIZON_YEARS);
  const ctx: GenerationContext = {
    anchorYmd,
    endByDate,
    endByCount,
    horizonEnd,
  };

  let targetDates: YMD[];
  try {
    switch (config.mode) {
      case 'DAILY':
        targetDates = generateInterval(ctx, 1);
        break;
      case 'WEEKLY':
        targetDates = generateInterval(ctx, 7);
        break;
      case 'MONTHLY':
        targetDates = generateMonthly(ctx, 1);
        break;
      case 'CUSTOM': {
        const c = config.custom!;
        if (c.frequency === 'DAILY') {
          targetDates = generateInterval(ctx, c.every);
        } else if (c.frequency === 'WEEKLY') {
          targetDates = generateCustomWeekly(ctx, c.every, c.weekdays!);
        } else {
          targetDates = generateMonthly(ctx, c.every);
        }
        break;
      }
      default:
        return { ok: false, error: `Unknown repeat mode: ${String(config.mode)}` };
    }
  } catch (err) {
    if (err instanceof CapError) return { ok: false, error: err.message };
    throw err;
  }

  if (targetDates.length === 0) {
    // The anchor itself didn't produce an occurrence — shouldn't happen, but guard anyway.
    return { ok: false, error: 'Repeat configuration produced no occurrences' };
  }

  // ── Materialize each date with the anchor's PT wall-clock ──────────────────
  const occurrences: AnchorSession[] = targetDates.map((targetYmd) => {
    const startUtc = ptWallToUtc(
      targetYmd.year,
      targetYmd.month,
      targetYmd.day,
      anchorPt.hour,
      anchorPt.minute,
      anchorPt.second,
      anchorPt.ms
    );
    const endTargetYmd = endDayOffset === 0 ? targetYmd : addDaysYmd(targetYmd, endDayOffset);
    const endUtc = ptWallToUtc(
      endTargetYmd.year,
      endTargetYmd.month,
      endTargetYmd.day,
      anchorEndPt.hour,
      anchorEndPt.minute,
      anchorEndPt.second,
      anchorEndPt.ms
    );
    return { startTime: startUtc.toISOString(), endTime: endUtc.toISOString() };
  });

  return { ok: true, occurrences };
}

// ─── Generation strategies ───────────────────────────────────────────────────

interface GenerationContext {
  anchorYmd: YMD;
  endByDate: YMD | null;
  endByCount: number | null;
  horizonEnd: YMD;
}

/**
 * Thrown internally when generation would exceed the per-series cap or horizon.
 * Caught at the top level and surfaced as a user-facing error.
 */
class CapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CapError';
  }
}

/** Generate occurrences at fixed day interval (1=Daily, 7=Weekly, N=Custom daily). */
function generateInterval(ctx: GenerationContext, dayInterval: number): YMD[] {
  const out: YMD[] = [ctx.anchorYmd];
  let cursor = ctx.anchorYmd;
  for (;;) {
    if (ctx.endByCount !== null && out.length >= ctx.endByCount) break;
    cursor = addDaysYmd(cursor, dayInterval);
    if (ctx.endByDate && compareYmd(cursor, ctx.endByDate) > 0) break;
    if (compareYmd(cursor, ctx.horizonEnd) > 0) {
      throw new CapError(`Series would extend beyond the ${SERIES_HORIZON_YEARS}-year horizon`);
    }
    out.push(cursor);
    if (out.length > MAX_SESSIONS_PER_SERIES) {
      throw new CapError(`Series exceeds the ${MAX_SESSIONS_PER_SERIES}-occurrence cap`);
    }
  }
  return out;
}

/**
 * Monthly generation. Walks month-by-month at `monthInterval` step. Months that don't
 * contain the anchor's day-of-month are SKIPPED (Apple Calendar behavior); skipped
 * months do not count against the After-N cap.
 */
function generateMonthly(ctx: GenerationContext, monthInterval: number): YMD[] {
  const out: YMD[] = [ctx.anchorYmd];
  let monthsAhead = 0;
  for (;;) {
    if (ctx.endByCount !== null && out.length >= ctx.endByCount) break;
    monthsAhead += monthInterval;
    // Walk monthsAhead months from anchor.
    const monthFromZero = ctx.anchorYmd.month - 1 + monthsAhead;
    const targetYear = ctx.anchorYmd.year + Math.floor(monthFromZero / 12);
    const targetMonth = (monthFromZero % 12) + 1;
    const dim = daysInMonth(targetYear, targetMonth);
    if (ctx.anchorYmd.day > dim) {
      // Skip this month — anchor's day doesn't exist here. Don't count against the cap.
      // We still need to check horizon to avoid spinning forever on a far-future cap.
      const probe: YMD = { year: targetYear, month: targetMonth, day: 1 };
      if (compareYmd(probe, ctx.horizonEnd) > 0) {
        throw new CapError(`Series would extend beyond the ${SERIES_HORIZON_YEARS}-year horizon`);
      }
      if (ctx.endByDate && compareYmd(probe, ctx.endByDate) > 0) break;
      continue;
    }
    const candidate: YMD = { year: targetYear, month: targetMonth, day: ctx.anchorYmd.day };
    if (ctx.endByDate && compareYmd(candidate, ctx.endByDate) > 0) break;
    if (compareYmd(candidate, ctx.horizonEnd) > 0) {
      throw new CapError(`Series would extend beyond the ${SERIES_HORIZON_YEARS}-year horizon`);
    }
    out.push(candidate);
    if (out.length > MAX_SESSIONS_PER_SERIES) {
      throw new CapError(`Series exceeds the ${MAX_SESSIONS_PER_SERIES}-occurrence cap`);
    }
  }
  return out;
}

/**
 * Custom Weekly generation per PRD §4.1.1.
 *  - Anchor is always occurrence #1, regardless of whether its weekday is in `weekdays`.
 *  - "Week bucket" = Sunday-start 7-day window. Anchor's bucket is #1; eligible buckets
 *    are #1, #1+N, #1+2N, …
 *  - In bucket #1, only generate dates strictly after the anchor that match `weekdays`.
 *  - In subsequent eligible buckets, generate all dates matching `weekdays` within the bucket.
 */
function generateCustomWeekly(
  ctx: GenerationContext,
  weekInterval: number,
  weekdays: Weekday[]
): YMD[] {
  const out: YMD[] = [ctx.anchorYmd];
  const weekdaySet = new Set<number>(weekdays);

  // Bucket #1 starts on the Sunday on/before anchor.
  const anchorWeekday = weekdayOfYmd(ctx.anchorYmd); // 0=Sun
  let bucketStart = addDaysYmd(ctx.anchorYmd, -anchorWeekday);

  let bucketIndex = 0; // 0 = anchor's bucket
  // Walk forward.
  for (;;) {
    if (ctx.endByCount !== null && out.length >= ctx.endByCount) break;
    for (let d = 0; d < 7; d++) {
      const candidate = addDaysYmd(bucketStart, d);
      if (bucketIndex === 0 && compareYmd(candidate, ctx.anchorYmd) <= 0) continue;
      if (!weekdaySet.has(weekdayOfYmd(candidate))) continue;
      if (ctx.endByDate && compareYmd(candidate, ctx.endByDate) > 0) return out;
      if (compareYmd(candidate, ctx.horizonEnd) > 0) {
        throw new CapError(`Series would extend beyond the ${SERIES_HORIZON_YEARS}-year horizon`);
      }
      out.push(candidate);
      if (out.length > MAX_SESSIONS_PER_SERIES) {
        throw new CapError(`Series exceeds the ${MAX_SESSIONS_PER_SERIES}-occurrence cap`);
      }
      if (ctx.endByCount !== null && out.length >= ctx.endByCount) return out;
    }
    // Advance to next eligible bucket.
    bucketIndex += weekInterval;
    bucketStart = addDaysYmd(bucketStart, 7 * weekInterval);
    // Defensive: stop if bucket start exceeds horizon — generateMonthly's same idea.
    if (compareYmd(bucketStart, ctx.horizonEnd) > 0) {
      throw new CapError(`Series would extend beyond the ${SERIES_HORIZON_YEARS}-year horizon`);
    }
    if (ctx.endByDate && compareYmd(bucketStart, ctx.endByDate) > 0) break;
  }
  return out;
}
